import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Sparkles } from 'lucide-react';
import { FiFacebook, FiInstagram } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: '',
};

const contactFormEndpoint = process.env.REACT_APP_CONTACT_FORM_ENDPOINT;

const serviceOptions = [
  { value: '', label: 'Select a service' },
  { value: '3D Box Letters', label: '3D Box Letters' },
  { value: 'Channel Letter', label: 'Channel Letter' },
  { value: 'ACP Board', label: 'ACP Board' },
  { value: 'Glow Sign Board', label: 'Glow Sign Board' },
  { value: 'Neon Signage', label: 'Neon Signage' },
  { value: 'LED Panels', label: 'LED Panels' },
  { value: 'Video Panel', label: 'Video Panel' },
  { value: 'Fabric Board', label: 'Fabric Board' },
  { value: 'Gifting Products', label: 'Gifting Products' },
];

function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (submitSuccess) setSubmitSuccess(false);
    if (submitError) setSubmitError('');
  };

  const openMailtoFallback = () => {
    const body = [
      `Name: ${formData.name}`,
      `Phone: ${formData.phone}`,
      `Email: ${formData.email}`,
      `Service: ${formData.service || 'Not specified'}`,
      '',
      formData.message || '(No additional message)',
    ].join('\n');
    const subject = encodeURIComponent(`Website enquiry — ${formData.name}`);
    const bodyEnc = encodeURIComponent(body);
    window.location.href = `mailto:shyamadvertising101@gmail.com?subject=${subject}&body=${bodyEnc}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (contactFormEndpoint) {
      setSubmitting(true);
      try {
        const res = await fetch(contactFormEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: formData.service || null,
            message: formData.message || null,
            _subject: `Website enquiry — ${formData.name}`,
          }),
        });
        if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
        setSubmitSuccess(true);
        setFormData(initialForm);
      } catch {
        setSubmitError(
          'We could not deliver your message automatically. Use email or WhatsApp in the sidebar, or try again.'
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    openMailtoFallback();
    setSubmitSuccess(true);
    setFormData(initialForm);
  };

  return (
    <section className="py-16 md:py-24 bg-surface relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(234,88,12,0.12),transparent_55%)]"
        aria-hidden
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12 md:mb-14">
          <p className="inline-flex items-center gap-2 text-brand-amber text-xs md:text-sm uppercase tracking-[0.2em] font-semibold mb-4">
            <Sparkles className="w-4 h-4 text-brand-yellow shrink-0" aria-hidden />
            Get in touch
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-1 mb-4">
            Contact <span className="text-gradient-brand">us</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            Have a project in mind? We&apos;d love to hear from you. Reach out for a free consultation and
            quote.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="rounded-2xl p-6 border border-white/10 bg-surface-elevated/60 backdrop-blur-sm sticky top-24 shadow-card-premium">
              <h2 className="text-xl font-bold text-white mb-6">Contact information</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-orange/15 p-3 rounded-full border border-brand-amber/20">
                    <MapPin className="w-5 h-5 text-brand-amber" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Visit us</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Shop No 63, V Mall, Thakur Complex,
                      <br />
                      Kandivali (E), Mumbai - 400101
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-orange/15 p-3 rounded-full border border-brand-amber/20">
                    <Phone className="w-5 h-5 text-brand-amber" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Call us</h3>
                    <a href="tel:9821466536" className="text-gray-400 hover:text-brand-yellow text-sm block transition-colors">
                      9821 466 536
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-orange/15 p-3 rounded-full border border-brand-amber/20">
                    <FaWhatsapp className="w-5 h-5 text-emerald-400" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                    <a
                      href="https://wa.me/919821466536"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
                    >
                      9821 466 536 — chat on WhatsApp
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-orange/15 p-3 rounded-full border border-brand-amber/20">
                    <Mail className="w-5 h-5 text-brand-amber" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <a
                      href="mailto:shyamadvertising101@gmail.com"
                      className="text-gray-400 hover:text-brand-yellow text-sm break-all underline-offset-2 hover:underline transition-colors"
                    >
                      shyamadvertising101@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-orange/15 p-3 rounded-full border border-brand-amber/20">
                    <Clock className="w-5 h-5 text-brand-amber" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Hours</h3>
                    <p className="text-gray-400 text-sm">
                      Monday - Saturday: 10:00 AM - 7:00 PM
                      <br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10">
                <h3 className="text-white font-semibold mb-4">Follow us</h3>
                <div className="flex gap-3">
                  <a
                    href="https://facebook.com/shamadvertising101"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/5 border border-white/10 hover:bg-brand-orange/20 hover:border-brand-amber/30 p-2 rounded-full transition-all duration-300"
                    aria-label="Facebook"
                  >
                    <FiFacebook className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href="https://instagram.com/shyam_advertising_101"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/5 border border-white/10 hover:bg-brand-orange/20 hover:border-brand-amber/30 p-2 rounded-full transition-all duration-300"
                    aria-label="Instagram"
                  >
                    <FiInstagram className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl p-6 md:p-8 border border-white/10 bg-surface-elevated/50 shadow-card-premium">
              <h2 className="text-2xl font-bold text-white mb-2">Send a message</h2>
              <p className="text-gray-400 mb-6 text-sm md:text-base">
                {contactFormEndpoint ? (
                  <>Your message is sent to our team. We aim to reply within one business day.</>
                ) : (
                  <>
                    Submitting opens your email app with a pre-filled message to us. You can edit before
                    sending.
                  </>
                )}
              </p>

              {submitError ? (
                <div className="mb-6 rounded-xl border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-200" role="alert">
                  {submitError}
                </div>
              ) : null}

              {submitSuccess ? (
                <div
                  className="mb-6 rounded-xl border border-brand-amber/30 bg-brand-orange/10 px-4 py-3 text-sm text-gray-200"
                  role="status"
                >
                  {contactFormEndpoint ? (
                    <>
                      Thank you — we received your message and will get back to you soon. You can also reach
                      us on{' '}
                      <a href="https://wa.me/919821466536" className="text-emerald-400 font-medium underline" target="_blank" rel="noopener noreferrer">
                        WhatsApp
                      </a>{' '}
                      for a faster reply.
                    </>
                  ) : (
                    <>
                      If your mail program didn&apos;t open, email{' '}
                      <a href="mailto:shyamadvertising101@gmail.com" className="text-brand-yellow font-medium underline">
                        shyamadvertising101@gmail.com
                      </a>{' '}
                      directly or message us on{' '}
                      <a href="https://wa.me/919821466536" className="text-emerald-400 font-medium underline" target="_blank" rel="noopener noreferrer">
                        WhatsApp
                      </a>
                      .
                    </>
                  )}
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-gray-300 text-sm mb-2">
                      Your name *
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      placeholder="Enter your name"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-brand-amber focus:ring-1 focus:ring-brand-amber/50 transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-gray-300 text-sm mb-2">
                      Phone number *
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                      placeholder="Enter your phone number"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-brand-amber focus:ring-1 focus:ring-brand-amber/50 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-email" className="block text-gray-300 text-sm mb-2">
                      Email address *
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      placeholder="Enter your email"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-brand-amber focus:ring-1 focus:ring-brand-amber/50 transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="block text-gray-300 text-sm mb-2">
                      Service required
                    </label>
                    <select
                      id="contact-service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-brand-amber focus:ring-1 focus:ring-brand-amber/50 transition cursor-pointer"
                      style={{ color: formData.service ? '#fff' : '#9ca3af' }}
                    >
                      {serviceOptions.map((opt) => (
                        <option key={opt.value || 'placeholder'} value={opt.value} className="bg-gray-900 text-white">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-gray-300 text-sm mb-2">
                    Your message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Tell us about your project: size, location, timeline, etc."
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-brand-amber focus:ring-1 focus:ring-brand-amber/50 transition resize-y min-h-[120px]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-xl bg-gradient-brand py-3.5 font-bold text-black shadow-brand-glow hover:opacity-95 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" aria-hidden />
                  {submitting ? 'Sending…' : contactFormEndpoint ? 'Send message' : 'Send message via email'}
                </button>

                {!contactFormEndpoint ? (
                  <button
                    type="button"
                    onClick={openMailtoFallback}
                    className="w-full text-sm text-gray-500 hover:text-brand-amber transition-colors"
                  >
                    Prefer only email app? Open prefilled mail without clearing the form
                  </button>
                ) : null}

                <p className="text-center text-xs text-gray-500">
                  We respect your privacy. Your details are used only to respond to your enquiry.
                </p>
              </form>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-white/10 bg-surface-elevated/40">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none z-10" aria-hidden />
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2703.0506369982536!2d72.86506277337296!3d19.213038647659765!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b730e3e2c76f%3A0x3348ea9c037dc126!2sShyam%20Advertising!5e1!3m2!1sen!2sin!4v1775561281854!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Shyam Advertising location on Google Maps"
                  className="w-full"
                />
              </div>
              <div className="p-4 border-t border-white/10 bg-black/30">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-brand-amber shrink-0" aria-hidden />
                  <p className="text-gray-300 text-sm">
                    Shop No 63, V Mall, Thakur Complex, Kandivali (E), Mumbai - 400101
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
